/**
 * AI Artifact Renderer
 * Detects and renders special AI artifacts (Excalidraw diagrams, Drive files, task file previews)
 * embedded in the assistant's markdown response.
 */

import { files_presign_path } from '../../routes.js'


/**
 * Parse artifact JSON blocks from assistant text.
 * Looks for JSON objects with artifact_type, drive_url, or files array.
 */
export function parseArtifacts(text) {
  const artifacts = []

  // Split on ``` boundaries and process every other segment (the ones inside fences).
  // This avoids regex quantifier issues with nested `}` characters in multi-level JSON.
  const parts = text.split('```')
  // parts[0] = before first fence, parts[1] = first fenced block, parts[2] = after, etc.
  for (let i = 1; i < parts.length; i += 2) {
    const block = parts[i]
    // Strip optional language tag (e.g. "json\n" or "json ")
    const content = block.startsWith('json') ? block.slice(4).trim() : block.trim()
    if (!content.startsWith('{')) continue
    try {
      const obj = JSON.parse(content)
      if (isArtifact(obj)) {
        // Reconstruct the raw fence block for stripping from rendered markdown
        const raw = '```' + block + '```'
        artifacts.push({ json: obj, raw, index: text.indexOf(raw) })
      }
    } catch (e) { /* not valid JSON */ }
  }

  return artifacts
}

function isArtifact(obj) {
  return isArtifactObject(obj)
}

export function isArtifactObject(obj) {
  if (!obj || typeof obj !== 'object') return false
  // Explicitly exclude Code Interpreter internal responses
  if ('exit_code' in obj || 'stdout' in obj || 'stderr' in obj) return false
  return (
    obj.artifact_type === 'excalidraw' ||
    (obj.success && obj.drive_url && obj.filename) ||
    (obj.success && Array.isArray(obj.files) && obj.task_id !== undefined) ||
    // Document tool errors: success:false with filename (drive save failed) — hide raw JSON
    (!obj.success && obj.filename && typeof obj.error === 'string')
  )
}

/**
 * Render an artifact as an HTML element.
 */
export function renderArtifact(artifactJson) {
  if (artifactJson.artifact_type === 'excalidraw') {
    return renderExcalidrawArtifact(artifactJson)
  }
  if (artifactJson.drive_url && artifactJson.filename) {
    return renderDriveFileArtifact(artifactJson)
  }
  if (Array.isArray(artifactJson.files) && artifactJson.task_id !== undefined) {
    return renderTaskFilesArtifact(artifactJson)
  }
  if (!artifactJson.success && artifactJson.filename) {
    return renderDocumentErrorArtifact(artifactJson)
  }
  return null
}

function renderExcalidrawArtifact(artifact) {
  const wrapper = document.createElement('div')
  wrapper.className = 'ai-artifact ai-artifact-excalidraw'

  // Header
  const header = document.createElement('div')
  header.className = 'ai-artifact-header'
  header.innerHTML = '<i class="bi bi-pencil-square me-2"></i>'
  const titleEl = document.createElement('span')
  titleEl.className = 'ai-artifact-title'
  titleEl.textContent = artifact.title || 'Diagram'
  header.appendChild(titleEl)
  wrapper.appendChild(header)

  // Loading overlay — visible while the iframe fetches and renders the PNG
  const loadingOverlay = document.createElement('div')
  loadingOverlay.className = 'ai-excalidraw-loading d-flex align-items-center gap-2 text-muted small p-3'
  loadingOverlay.innerHTML =
    '<span class="spinner-grow spinner-grow-sm" role="status" aria-hidden="true"></span>' +
    '<span class="spinner-grow spinner-grow-sm" role="status" aria-hidden="true" style="animation-delay:0.15s"></span>' +
    '<span class="spinner-grow spinner-grow-sm" role="status" aria-hidden="true" style="animation-delay:0.3s"></span>'
  wrapper.appendChild(loadingOverlay)

  // Render via @excalidraw/excalidraw CDN inside a blob: URL iframe.
  const sceneData = artifact.excalidraw_data || { elements: [], appState: {}, files: {} }
  const html = buildExcalidrawHtml(sceneData)
  const blob = new Blob([html], { type: 'text/html' })
  const blobUrl = URL.createObjectURL(blob)

  const iframe = document.createElement('iframe')
  iframe.setAttribute('src', blobUrl)
  iframe.setAttribute('scrolling', 'no')
  iframe.setAttribute('frameborder', '0')
  iframe.style.cssText = 'display:none;width:100%;min-height:200px;height:400px;border:1px solid var(--bs-border-color);border-radius:0.375rem;margin-top:0.5rem;background:#fff;'
  iframe.addEventListener('load', () => {
    URL.revokeObjectURL(blobUrl)
  }, { once: true })

  const onMessage = (event) => {
    if (event.data?.excalidrawHeight && event.source === iframe.contentWindow) {
      const h = event.data.excalidrawHeight
      if (h > 10) iframe.style.height = (h + 32) + 'px'
      // Hide spinner, show iframe
      loadingOverlay.remove()
      iframe.style.display = 'block'
      window.removeEventListener('message', onMessage)
    }
  }
  window.addEventListener('message', onMessage)

  wrapper.appendChild(iframe)
  return wrapper
}

function buildExcalidrawHtml(sceneData) {
  const sceneJson = JSON.stringify(sceneData)
  // Uses convertToExcalidrawElements (same approach as the reference sample project)
  // then exportToBlob to get a PNG — no React editor, just a static image.
  // React + @excalidraw/excalidraw are loaded from esm.sh inside the sandboxed iframe.
  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<script>window.EXCALIDRAW_ASSET_PATH = "https://esm.sh/@excalidraw/excalidraw@0.18.0/dist/prod/";<\/script>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { background: #fff; display: flex; align-items: flex-start; justify-content: center; }
  img { max-width: 100%; height: auto; display: block; }
  #error { color: red; font-family: monospace; font-size: 12px; padding: 8px; white-space: pre-wrap; }
</style>
</head>
<body>
<div id="out"></div>
<div id="error" style="display:none"></div>
<script type="module">
  import { convertToExcalidrawElements, exportToBlob } from "https://esm.sh/@excalidraw/excalidraw@0.18.0?bundle-deps";
  const scene = ${sceneJson};
  try {
    const elements = convertToExcalidrawElements(scene.elements || []);
    const blob = await exportToBlob({
      elements,
      appState: {
        exportBackground: true,
        viewBackgroundColor: "#ffffff",
        ...(scene.appState || {}),
      },
      files: scene.files || {},
      mimeType: "image/png",
    });
    const url = URL.createObjectURL(blob);
    const img = document.createElement("img");
    img.src = url;
    img.onload = () => {
      document.getElementById("out").appendChild(img);
      // Signal height to parent
      parent.postMessage({ excalidrawHeight: img.naturalHeight || img.offsetHeight }, "*");
    };
  } catch (e) {
    const err = document.getElementById("error");
    err.style.display = "block";
    err.textContent = "Render error: " + e.message;
    parent.postMessage({ excalidrawHeight: 80 }, "*");
  }
<\/script>
</body>
</html>`
}

function renderDocumentErrorArtifact(artifact) {
  const container = document.createElement('div')
  container.className = 'ai-artifact ai-artifact-drive-file ai-artifact-error'

  const icon = getFileIcon(artifact.filename || '')
  const filename = artifact.filename || 'Fájl'

  const header = document.createElement('div')
  header.className = 'ai-artifact-header'
  header.innerHTML = `<i class="${icon} me-2"></i>`
  const title = document.createElement('span')
  title.className = 'ai-artifact-title'
  title.textContent = filename
  header.appendChild(title)
  const badge = document.createElement('span')
  badge.className = 'badge bg-danger ms-2'
  badge.textContent = 'Hiba'
  header.appendChild(badge)

  const errorMsg = document.createElement('div')
  errorMsg.className = 'text-danger small mt-1'
  errorMsg.textContent = artifact.error || 'A dokumentum mentése sikertelen.'

  container.appendChild(header)
  container.appendChild(errorMsg)
  return container
}

function renderDriveFileArtifact(artifact) {
  const container = document.createElement('div')
  container.className = 'ai-artifact ai-artifact-drive-file'

  const icon = getFileIcon(artifact.filename || '')
  const sizeText = artifact.byte_size ? formatBytes(artifact.byte_size) : ''
  const filename = artifact.filename || 'Fájl'

  // Build header
  const header = document.createElement('div')
  header.className = 'ai-artifact-header'
  header.innerHTML = `<i class="${icon} me-2"></i>`
  const title = document.createElement('span')
  title.className = 'ai-artifact-title'
  title.textContent = filename
  header.appendChild(title)
  if (sizeText) {
    const size = document.createElement('span')
    size.className = 'text-muted small ms-2'
    size.textContent = sizeText
    header.appendChild(size)
  }
  const badge = document.createElement('span')
  badge.className = 'badge bg-success ms-2'
  badge.textContent = 'Drive - AI mappa'
  header.appendChild(badge)

  // Build actions — placeholder spinner while resolving URL
  const actions = document.createElement('div')
  actions.className = 'ai-artifact-actions mt-2'
  actions.innerHTML =
    '<span class="spinner-grow spinner-grow-sm me-1" role="status" aria-hidden="true"></span>' +
    '<span class="text-muted small">URL feloldása...</span>'

  container.appendChild(header)
  container.appendChild(actions)

  // Resolve backend://file/ URI (or fall back to the raw URL if already a real URL)
  const rawUrl = artifact.drive_url || '#'
  const resolvePromise = isPresignableMcpFileUri(rawUrl)
    ? resolvePresignableFileUris([rawUrl]).then(resolved => resolved[rawUrl] || '#')
    : Promise.resolve(rawUrl)

  resolvePromise.then(driveUrl => {
    actions.innerHTML = ''

    const downloadBtn = document.createElement('a')
    downloadBtn.setAttribute('href', driveUrl)
    downloadBtn.setAttribute('target', '_blank')
    downloadBtn.setAttribute('download', filename)
    downloadBtn.className = 'btn btn-sm btn-outline-primary me-2'
    downloadBtn.innerHTML = '<i class="bi bi-download me-1"></i>Letöltés'

    const openBtn = document.createElement('a')
    openBtn.setAttribute('href', driveUrl)
    openBtn.setAttribute('target', '_blank')
    openBtn.className = 'btn btn-sm btn-outline-secondary'
    openBtn.innerHTML = '<i class="bi bi-eye me-1"></i>Megnyitás'

    actions.appendChild(downloadBtn)
    actions.appendChild(openBtn)
  })

  return container
}

function isPresignableMcpFileUri(url) {
  return typeof url === 'string' && url.startsWith('backend://file/')
}

function collectPresignableFileUris(files) {
  const uris = new Set()
  for (const f of files) {
    if (isPresignableMcpFileUri(f.url)) uris.add(f.url)
    if (isPresignableMcpFileUri(f.preview_image_url)) uris.add(f.preview_image_url)
  }
  return [...uris]
}

async function resolvePresignableFileUris(uris) {
  if (!uris.length) return {}
  const url = new URL(files_presign_path(), window.location.origin)
  uris.forEach(u => url.searchParams.append('uris[]', u))
  try {
    const resp = await fetch(url, {
      headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
    })
    if (!resp.ok) return {}
    const data = await resp.json()
    return data.resolved || {}
  } catch (e) {
    console.warn('MCP file URI (backend://file/) resolution failed:', e)
    return {}
  }
}

function buildFileItem(f, resolved, galleryId) {
  const fname = f.filename || f.name || 'Fájl'
  const rawUrl = f.url || '#'
  const rawPreview = f.preview_image_url
  const fullUrl = isPresignableMcpFileUri(rawUrl) ? (resolved[rawUrl] || '#') : rawUrl
  const previewUrl = rawPreview
    ? (isPresignableMcpFileUri(rawPreview) ? (resolved[rawPreview] || null) : rawPreview)
    : null
  const isImage = f.is_image || (f.content_type || '').startsWith('image/')
  const isPdf = (f.content_type || '') === 'application/pdf'

  // Outer wrappers
  const item = document.createElement('div')
  item.className = 'file-item d-flex flex-column me-1 mb-2 me-md-2 mb-md-2'
  const card = document.createElement('div')
  card.className = 'card shadow-sm h-100 file-card file-card-fixed-width'
  const body = document.createElement('div')
  body.className = 'card-body p-1 d-flex flex-column align-items-center'

  // Link — set href via setAttribute so URLs pass through unescaped
  const link = document.createElement('a')
  link.setAttribute('href', fullUrl)
  link.className = 'd-block text-decoration-none'
  if (isImage) {
    link.dataset.photoswipe = galleryId
    link.dataset.caption = fname
    link.dataset.type = 'image'
  } else if (isPdf) {
    link.dataset.photoswipe = galleryId
    link.dataset.caption = fname
    link.dataset.type = 'iframe'
  }

  // Thumbnail container
  const thumb = document.createElement('div')
  thumb.className = 'border rounded p-1 d-inline-block thumbnail-container-proportional'
  thumb.style.cssText = 'width:130px;height:100px;'

  if (previewUrl) {
    const img = document.createElement('img')
    img.setAttribute('src', previewUrl)
    img.className = 'img-fluid rounded'
    img.alt = fname
    img.loading = 'lazy'
    thumb.appendChild(img)
  } else {
    const icon = document.createElement('div')
    icon.className = 'd-flex align-items-center justify-content-center bg-light rounded p-3'
    icon.innerHTML = `<i class="${getFileIcon(fname)} fs-1 text-muted"></i>`
    thumb.appendChild(icon)
  }
  link.appendChild(thumb)
  body.appendChild(link)

  // Filename label
  const nameEl = document.createElement('small')
  nameEl.className = 'text-truncate mt-1 w-100 text-center'
  nameEl.style.maxWidth = '130px'
  nameEl.title = fname
  nameEl.textContent = fname
  body.appendChild(nameEl)

  // File group label
  if (f.file_group_name) {
    const groupEl = document.createElement('small')
    groupEl.className = 'text-muted text-truncate w-100 text-center'
    groupEl.style.maxWidth = '130px'
    groupEl.textContent = f.file_group_name
    body.appendChild(groupEl)
  }

  card.appendChild(body)
  item.appendChild(card)
  return item
}

function renderTaskFilesArtifact(artifact) {
  const container = document.createElement('div')
  container.className = 'ai-artifact ai-artifact-task-files'

  // Header
  const header = document.createElement('div')
  header.className = 'ai-artifact-header'
  header.innerHTML = '<i class="bi bi-paperclip me-2"></i>'
  const title = document.createElement('span')
  title.className = 'ai-artifact-title'
  title.textContent = `Feladat #${artifact.task_id} fájljai`
  const badge = document.createElement('span')
  badge.className = 'badge bg-info ms-2'
  badge.textContent = `${artifact.file_count || 0} fájl`
  header.appendChild(title)
  header.appendChild(badge)
  container.appendChild(header)

  // Gallery — Stimulus photoswipe controller picks this up
  const galleryId = `ai-task-files-${artifact.task_id}-${Date.now()}`
  const gallery = document.createElement('div')
  gallery.id = galleryId
  gallery.className = 'd-flex flex-row flex-wrap text-center mt-2'
  gallery.dataset.controller = 'photoswipe'
  gallery.innerHTML = '<div class="d-flex align-items-center gap-2 text-muted small p-2">' +
    '<span class="spinner-grow spinner-grow-sm" role="status" aria-hidden="true"></span>' +
    '<span class="spinner-grow spinner-grow-sm" role="status" aria-hidden="true" style="animation-delay:0.15s"></span>' +
    '<span class="spinner-grow spinner-grow-sm" role="status" aria-hidden="true" style="animation-delay:0.3s"></span>' +
    '</div>'
  container.appendChild(gallery)

  const files = artifact.files || []
  resolvePresignableFileUris(collectPresignableFileUris(files)).then(resolved => {
    gallery.innerHTML = ''
    if (!files.length) {
      const empty = document.createElement('p')
      empty.className = 'text-muted small'
      empty.textContent = 'Nincsenek fájlok.'
      gallery.appendChild(empty)
    } else {
      files.forEach(f => gallery.appendChild(buildFileItem(f, resolved, galleryId)))
    }
    // Stimulus auto-connects controllers when elements are added to a connected DOM node.
    // No manual reconnect needed — data-controller="photoswipe" on gallery handles it.
  })

  return container
}


function getFileIcon(filename) {
  const ext = (filename.split('.').pop() || '').toLowerCase()
  const icons = {
    xlsx: 'bi bi-file-earmark-spreadsheet text-success',
    xls: 'bi bi-file-earmark-spreadsheet text-success',
    docx: 'bi bi-file-earmark-word text-primary',
    doc: 'bi bi-file-earmark-word text-primary',
    pptx: 'bi bi-file-earmark-slides text-warning',
    ppt: 'bi bi-file-earmark-slides text-warning',
    pdf: 'bi bi-file-earmark-pdf text-danger',
    png: 'bi bi-file-earmark-image text-info',
    jpg: 'bi bi-file-earmark-image text-info',
    jpeg: 'bi bi-file-earmark-image text-info',
    gif: 'bi bi-file-earmark-image text-info',
  }
  return icons[ext] || 'bi bi-file-earmark'
}

function formatBytes(bytes) {
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}

const _escapeEl = document.createElement('span')
function escapeHtml(str) {
  _escapeEl.textContent = String(str)
  return _escapeEl.innerHTML
}

